import { getAppConfig } from '../config/runtimeConfig';

const STORAGE = {
  pkceVerifier: 'onecloud.pkce.verifier',
  oauthState: 'onecloud.oauth.state',
  oauthNext: 'onecloud.oauth.next',
  tokens: 'onecloud.auth.tokens',
};

function normalizeBaseUrl(domainOrBaseUrl, region) {
  if (!domainOrBaseUrl) return null;
  if (domainOrBaseUrl.startsWith('http://') || domainOrBaseUrl.startsWith('https://')) {
    return domainOrBaseUrl.replace(/\/+$/, '');
  }
  return `https://${domainOrBaseUrl}.auth.${region}.amazoncognito.com`;
}

export function getCognitoConfig() {
  const region = getAppConfig('REACT_APP_COGNITO_REGION');
  const clientId = getAppConfig('REACT_APP_COGNITO_CLIENT_ID');
  const domain = getAppConfig('REACT_APP_COGNITO_DOMAIN');
  const redirectUri = getAppConfig('REACT_APP_COGNITO_REDIRECT_URI');
  const logoutUri = getAppConfig('REACT_APP_COGNITO_LOGOUT_URI');
  const scopes = (
    getAppConfig('REACT_APP_COGNITO_SCOPES') ||
    'openid email profile aws.cognito.signin.user.admin'
  ).trim();
  const userPoolId = getAppConfig('REACT_APP_COGNITO_USER_POOL_ID');

  const baseUrl = normalizeBaseUrl(domain, region);
  const missing = [];
  if (!region) missing.push('REACT_APP_COGNITO_REGION');
  if (!clientId) missing.push('REACT_APP_COGNITO_CLIENT_ID');
  if (!domain) missing.push('REACT_APP_COGNITO_DOMAIN');
  if (!redirectUri) missing.push('REACT_APP_COGNITO_REDIRECT_URI');
  if (!logoutUri) missing.push('REACT_APP_COGNITO_LOGOUT_URI');

  return {
    region,
    clientId,
    domain,
    baseUrl,
    redirectUri,
    logoutUri,
    scopes,
    userPoolId,
    configured: missing.length === 0,
    missing,
  };
}

function base64UrlEncodeBytes(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomUrlSafeString(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncodeBytes(bytes);
}

async function sha256Base64Url(input) {
  const data = new TextEncoder().encode(input);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncodeBytes(new Uint8Array(hash));
}

function setSessionItem(key, value) {
  window.sessionStorage.setItem(key, value);
}

function getSessionItem(key) {
  return window.sessionStorage.getItem(key);
}

function removeSessionItem(key) {
  window.sessionStorage.removeItem(key);
}

export function getStoredTokens() {
  try {
    const raw = window.localStorage.getItem(STORAGE.tokens);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearStoredTokens() {
  window.localStorage.removeItem(STORAGE.tokens);
}

export function storeTokens(tokens) {
  if (!tokens) {
    clearStoredTokens();
    return;
  }
  window.localStorage.setItem(STORAGE.tokens, JSON.stringify(tokens));
}

export function isAccessTokenUsable(tokens) {
  if (!tokens?.accessToken || !tokens?.expiresAtMs) return false;
  return Date.now() < tokens.expiresAtMs - 30_000;
}

export function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== 'string' || !jwt.includes('.')) return null;
  try {
    const payload = jwt.split('.')[1];
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export async function startCognitoLogin({ nextPath = '/' } = {}) {
  const config = getCognitoConfig();
  if (!config.configured) {
    throw new Error(`Missing Cognito config: ${config.missing.join(', ')}`);
  }
  const state = randomUrlSafeString(16);
  const verifier = randomUrlSafeString(64);
  const challenge = await sha256Base64Url(verifier);

  setSessionItem(STORAGE.oauthState, state);
  setSessionItem(STORAGE.oauthNext, nextPath);
  setSessionItem(STORAGE.pkceVerifier, verifier);

  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    redirect_uri: config.redirectUri,
    scope: config.scopes,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`${config.baseUrl}/oauth2/authorize?${params.toString()}`);
}

async function exchangeCodeForTokens({ code, codeVerifier }) {
  const config = getCognitoConfig();
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: codeVerifier,
  });

  const response = await fetch(`${config.baseUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error_description || data?.error || `Token exchange failed (${response.status})`;
    throw new Error(message);
  }

  const obtainedAtMs = Date.now();
  const expiresAtMs = obtainedAtMs + (Number(data.expires_in || 0) * 1000 || 0);

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    tokenType: data.token_type,
    expiresIn: data.expires_in,
    obtainedAtMs,
    expiresAtMs,
  };
}

export async function refreshCognitoTokens(refreshToken) {
  if (!refreshToken) {
    throw new Error('Missing refresh token. Please sign in again.');
  }

  const config = getCognitoConfig();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(`${config.baseUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error_description || data?.error || `Token refresh failed (${response.status})`;
    throw new Error(message);
  }

  const obtainedAtMs = Date.now();
  const expiresAtMs = obtainedAtMs + (Number(data.expires_in || 0) * 1000 || 0);

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken,
    tokenType: data.token_type,
    expiresIn: data.expires_in,
    obtainedAtMs,
    expiresAtMs,
  };
}

async function cognitoIdentityProviderRequest(target, payload) {
  const config = getCognitoConfig();
  if (!config.region) {
    throw new Error('Missing Cognito region configuration.');
  }

  const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.message || data?.__type || `${target} failed (${response.status})`;
    throw new Error(message);
  }
  return data;
}

export async function getCognitoUser(accessToken) {
  if (!accessToken) {
    throw new Error('Missing access token. Please sign in again.');
  }
  return cognitoIdentityProviderRequest('GetUser', { AccessToken: accessToken });
}

export async function updateCognitoUserAttributes(accessToken, attributes) {
  if (!accessToken) {
    throw new Error('Missing access token. Please sign in again.');
  }

  const userAttributes = Object.entries(attributes)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([Name, value]) => ({ Name, Value: String(value) }));

  if (!userAttributes.length) {
    throw new Error('No profile attributes were provided.');
  }

  return cognitoIdentityProviderRequest('UpdateUserAttributes', {
    AccessToken: accessToken,
    UserAttributes: userAttributes,
  });
}

export async function changeCognitoPassword(accessToken, previousPassword, proposedPassword) {
  if (!accessToken) {
    throw new Error('Missing access token. Please sign in again.');
  }

  return cognitoIdentityProviderRequest('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

export async function handleCognitoCallback({ code, state }) {
  const expectedState = getSessionItem(STORAGE.oauthState);
  const nextPath = getSessionItem(STORAGE.oauthNext) || '/';
  const verifier = getSessionItem(STORAGE.pkceVerifier);

  if (!expectedState || !verifier) {
    throw new Error('Missing PKCE session. Please try logging in again.');
  }
  if (!state || state !== expectedState) {
    throw new Error('Invalid state returned from Cognito. Please try logging in again.');
  }
  if (!code) {
    throw new Error('Missing authorization code from Cognito.');
  }

  const tokens = await exchangeCodeForTokens({ code, codeVerifier: verifier });
  storeTokens(tokens);

  removeSessionItem(STORAGE.oauthState);
  removeSessionItem(STORAGE.oauthNext);
  removeSessionItem(STORAGE.pkceVerifier);

  return { tokens, nextPath };
}

export function buildCognitoLogoutUrl() {
  const config = getCognitoConfig();
  if (!config.configured) return null;

  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri,
  });
  return `${config.baseUrl}/logout?${params.toString()}`;
}
